// src/components/local/HabilitacionLocalList.js
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InputGroup, FormControl, Table, Badge, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

import HabilitacionLocalForm from "./HabilitacionLocalForm";

import { ensureGestorAfiliado, fetchLogsByAfiliado } from "../../reducers/habilitacionLocal_slice";
import { fetchAfiliadosByLugar } from "../../reducers/afiliado_slice";
import { fetchLugarById } from "../../reducers/lugar_slice";
import CustomPrintModal from "../UI/CustomPrintModal";

const SITUACION_TRASLADO_LOCAL_ID = 3;

// Aux: obtener el último movimiento a INACTIVO de la cache de logs
const getUltimoInactivo = (logsEntry) => {
  if (!logsEntry || !Array.isArray(logsEntry.rows)) return null;
  const rows = [...logsEntry.rows].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  const found = rows.find((r) => (r?.hacia_estado || "").toUpperCase() === "INACTIVO");
  if (!found) return null;
  return {
    motivo: found.motivo || "",
    fecha: new Date(found.createdAt),
  };
};

const HabilitacionLocalList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const sesion = useSelector((s) => s.sesion);
  const afiliadoGestor = useSelector((s) => s.afiliado.current);
  const { afiliadosByLugar, statusGet } = useSelector((s) => s.afiliado);
  const lugarActual = useSelector((s) => s.lugar.current);
  const logsByAfiliado = useSelector((s) => s.habilitacionLocal.logsByAfiliado || {});

  const [search, setSearch] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("TODOS"); // TODOS | ACTIVO | INACTIVO
  const [selected, setSelected] = useState(null);

  // Orden (click en cabeceras con icono)
  const [sortConfig, setSortConfig] = useState({ key: "apellido1", direction: "asc" });
  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };
  const sortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up ms-1" />;
    return sortConfig.direction === "asc" ? (
      <i className="bi bi-caret-up-fill ms-1" />
    ) : (
      <i className="bi bi-caret-down-fill ms-1" />
    );
  };

  // 1) Asegurar afiliado del gestor -> id_lugar_residencia
  useEffect(() => {
    if (sesion?.id_persona) {
      dispatch(ensureGestorAfiliado(sesion.id_persona));
    }
  }, [dispatch, sesion?.id_persona]);

  // 2) Con el lugar del gestor, pedir afiliados y nombre del lugar (si hace falta)
  useEffect(() => {
    const idLugarGestor = Number(afiliadoGestor?.id_lugar_residencia);
    if (idLugarGestor) {
      dispatch(fetchAfiliadosByLugar(idLugarGestor));
      if (!lugarActual || Number(lugarActual.id) !== idLugarGestor) {
        dispatch(fetchLugarById(idLugarGestor));
      }
    }
  }, [dispatch, afiliadoGestor?.id_lugar_residencia]);

  // 3) Filtro: solo TRASLADO LOCAL + por estado + búsqueda
  const filteredBase = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    return (afiliadosByLugar || [])
      .filter((a) => Number(a?.id_situacion) === SITUACION_TRASLADO_LOCAL_ID)
      .filter((a) => {
        const estado = (a?.activo || "INACTIVO").toUpperCase();
        if (estadoFiltro === "ACTIVO") return estado === "ACTIVO";
        if (estadoFiltro === "INACTIVO") return estado === "INACTIVO";
        return true; // TODOS
      })
      .filter((a) => {
        if (!q) return true;
        const texto = [
          a?.num_reg,
          a?.persona?.nombre,
          a?.persona?.apellido1,
          a?.persona?.apellido2,
          a?.persona?.ci,
        ]
          .map((v) => (v || "").toString().toLowerCase())
          .join(" ");
        return texto.includes(q);
      });
  }, [afiliadosByLugar, estadoFiltro, search]);

  // 4) Orden simple
  const filtered = useMemo(() => {
    const arr = [...filteredBase];
    arr.sort((a, b) => {
      const getKey = (item) => {
        switch (sortConfig.key) {
          case "num_reg":
            return Number(item?.num_reg) || 0;
          case "ci":
            return (item?.persona?.ci || "").toString();
          case "estado":
            return (item?.activo || "INACTIVO").toString();
          case "apellido1":
          default:
            return (item?.persona?.apellido1 || "").toString();
        }
      };
      const av = getKey(a);
      const bv = getKey(b);
      if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
      if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredBase, sortConfig]);

  // Título: COLEGIO LOCAL — <Nombre del lugar>
  const nombreLugar = useMemo(() => {
    if (lugarActual?.nombre) return lugarActual.nombre;
    const first = afiliadosByLugar?.[0];
    return first?.lugar_residencia?.nombre || "—";
  }, [lugarActual?.nombre, afiliadosByLugar]);

  const handleBack = () => navigate("/admin");

  // -------- Impresión (mismo contrato que LugarList/AfiliadoList) ----------
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [] });

  // Prefetch de logs para los afiliados visibles (solo INACTIVOS — ahí se requiere motivo/fecha)
  const prefetchLogsForVisible = async () => {
    const inactivos = filtered.filter((a) => (a?.activo || "INACTIVO").toUpperCase() === "INACTIVO");
    const tasks = inactivos
      .map((a) => a.id)
      .filter((id) => !logsByAfiliado[id]?.rows)
      .map((id) => dispatch(fetchLogsByAfiliado(id)));
    if (tasks.length) await Promise.all(tasks);
  };

  // Construye el contenido para imprimir/exportar
  const buildRowsForExport = () => {
    const head = [
      ["Núm. Reg.", "Apellidos y Nombres", "CI", "Estado", "Motivo INACTIVO", "Desde (Fecha)", "Desde (Hora)"],
    ];
    const body = filtered.map((a) => {
      const estado = (a?.activo || "INACTIVO").toUpperCase();
      const fullName = `${a?.persona?.apellido1 || ""} ${a?.persona?.apellido2 || ""}, ${a?.persona?.nombre || ""}`.trim();
      let motivo = "";
      let desdeFecha = "";
      let desdeHora = "";

      if (estado === "INACTIVO") {
        const entry = logsByAfiliado[a.id];
        const last = getUltimoInactivo(entry);
        if (last) {
          motivo = last.motivo || "";
          desdeFecha = last.fecha.toLocaleDateString();
          desdeHora = last.fecha.toLocaleTimeString();
        }
      }

      return [
        a?.num_reg || "",
        fullName,
        a?.persona?.ci || "",
        estado,
        motivo,
        desdeFecha,
        desdeHora,
      ];
    });

    return { head, body };
  };

  const handlePreviewPrint = async () => {
    await prefetchLogsForVisible();
    const content = buildRowsForExport();
    setPrintContent(content);
    setShowPreviewModal(true);
  };

  const handleExportExcel = async () => {
    await prefetchLogsForVisible();
    const { body } = buildRowsForExport();
    // Armamos arreglo de objetos para que XLSX ponga cabecera
    const rows = body.map((r) => ({
      "Núm. Reg.": r[0],
      "Apellidos y Nombres": r[1],
      CI: r[2],
      Estado: r[3],
      "Motivo INACTIVO": r[4],
      "Desde (Fecha)": r[5],
      "Desde (Hora)": r[6],
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Afiliados");
    const ts = new Date();
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    const fileName = `colegio_local_${(nombreLugar || "").replace(/\s+/g, "_").toLowerCase()}_${ts.getFullYear()}${pad(
      ts.getMonth() + 1
    )}${pad(ts.getDate())}_${pad(ts.getHours())}${pad(ts.getMinutes())}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="container mt-3">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between">
        <h4 className="mb-0">COLEGIO LOCAL — {nombreLugar}</h4>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" title="Volver" onClick={handleBack}>
            <i className="bi bi-arrow-left"></i> Volver
          </Button>
          <Button
            variant="outline-primary"
            title="Imprimir"
            onClick={handlePreviewPrint}
            disabled={statusGet === "loading" || filtered.length === 0}
          >
            <i className="bi bi-printer"></i> Imprimir
          </Button>
          <Button
            variant="outline-success"
            title="Exportar a Excel"
            onDoubleClick={handleExportExcel}
            disabled={statusGet === "loading" || filtered.length === 0}
          >
            <i className="bi bi-file-earmark-excel"></i> Exportar
          </Button>
        </div>
      </div>

      {/* Controles de filtro/búsqueda */}
      <div className="row mt-3 g-2">
        <div className="col-md-6">
          <InputGroup>
            <FormControl
              placeholder="Buscar por nombre, apellido, CI o núm. reg."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </div>
        <div className="col-md-3">
          <Form.Select value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)}>
            <option value="TODOS">Todos</option>
            <option value="ACTIVO">ACTIVO</option>
            <option value="INACTIVO">INACTIVO</option>
          </Form.Select>
        </div>
      </div>

      {/* Estado de carga */}
      {statusGet === "loading" && <div className="alert alert-info mt-3">Cargando afiliados…</div>}

      {/* Tabla */}
      <div className="table-responsive mt-3" style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <Table striped hover size="sm">
          <thead>
            <tr>
              <th>#</th>
              <th style={{ cursor: "pointer" }} onClick={() => toggleSort("num_reg")}>
                Núm. Reg. {sortIcon("num_reg")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => toggleSort("apellido1")}>
                Afiliado {sortIcon("apellido1")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => toggleSort("ci")}>
                CI {sortIcon("ci")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => toggleSort("estado")}>
                Estado {sortIcon("estado")}
              </th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((a, idx) => {
              const estado = (a?.activo || "INACTIVO").toUpperCase();
              const fullName = `${a?.persona?.apellido1 || ""} ${a?.persona?.apellido2 || ""}, ${
                a?.persona?.nombre || ""
              }`.trim();
              return (
                <tr key={a.id}>
                  <td>{idx + 1}</td>
                  <td>{a?.num_reg || "—"}</td>
                  <td>{fullName}</td>
                  <td>{a?.persona?.ci || "—"}</td>
                  <td>
                    <Badge bg={estado === "ACTIVO" ? "success" : "secondary"}>{estado}</Badge>
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      title="Gestionar habilitación local"
                      onClick={() => setSelected(a)}
                    >
                      Gestionar
                    </Button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && statusGet !== "loading" && (
              <tr>
                <td colSpan="6" className="text-muted">
                  Sin resultados.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Modal de gestión */}
      {selected && <HabilitacionLocalForm afiliado={selected} onClose={() => setSelected(null)} />}

      {/* Modal de impresión */}
      <CustomPrintModal
        show={showPreviewModal}
        handleClose={() => setShowPreviewModal(false)}
        title1={`COLEGIO LOCAL — ${nombreLugar}`}
        title2={`Total: ${filtered.length}`}
        content={printContent}
        copia={false}
      />
    </div>
  );
};

export default HabilitacionLocalList;
